<script setup lang="ts">
// TTS debug panel (round-30, 2026-09-01) — sibling to DebugStt.vue (speech-IN debugging), this is
// speech-OUT debugging: everything needed to tune/verify the server-side Piper voice
// (server/app/services/tts_service.py) without reproducing a live corner-claim or roll-call at
// the table. Reached the same way as DebugStt.vue: Settings (gear icon) > "TTS debug panel",
// never in the main nav tab bar — see components/SettingsPanel.vue.
//
// Five panels, in the order a debugging session actually wants them:
//  1. Free-text synth — type anything, hear it, see cache/timing.
//  2. Template previews — the REAL production prompts (same text-fn + cache key as the live
//     confirm-name/roll-call endpoints), for a real player, cached variants included.
//  3. Cache inspector — what's on disk, hit counts, play/purge (purge is X-Admin-Token-gated,
//     same guard app/routers/admin.py's destructive endpoints use).
//  4. Playback diagnostics — browser audio state, a TTS-independent test tone, last N TTS calls
//     across every flow.
//  5. Voice comparison — same text across every installed voice, side by side. Voice-agnostic by
//     construction (server/app/services/tts_service.py's list_voices() auto-discovers from
//     tts_models/) — a voice added later just shows up here, no code change.
import { onMounted, reactive, ref, watch } from 'vue'
import {
  ApiRequestError,
  getPlayers,
  getTtsCache,
  getTtsEvents,
  getTtsTemplates,
  getTtsVoices,
  purgeAllTtsCache,
  purgeTtsCacheEntry,
  ttsCacheAudioUrl,
  ttsPreviewUrl,
  ttsSpeakUrl,
} from '../backend'
import { pushToast } from '../store/toast'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import type { Player, TtsCacheEntry, TtsEvent, TtsTemplate, TtsVoice } from '../types'

type Lang = 'es' | 'en'

// ---------------------------------------------------------------------------
// Shared synth-and-play helper — every panel below (free text, template preview, voice
// comparison) funnels through this so the timing/cache-hit story is measured identically
// everywhere: request -> response headers (X-Tts-Cache tells us hit/miss directly, no guessing
// from latency) -> decoded bytes -> <audio> loadedmetadata (real playable duration).
// ---------------------------------------------------------------------------

interface SynthResult {
  ok: boolean
  cached: boolean | null // null = no X-Tts-Cache header (network/HTTP failure before a response)
  requestToHeadersMs: number
  headersToBytesMs: number | null
  totalMs: number | null
  sizeBytes: number | null
  durationSec: number | null
  error?: string
}

async function synthesizeAndPlay(url: string): Promise<SynthResult> {
  const t0 = performance.now()
  let res: Response
  try {
    res = await fetch(url)
  } catch {
    return {
      ok: false,
      cached: null,
      requestToHeadersMs: performance.now() - t0,
      headersToBytesMs: null,
      totalMs: null,
      sizeBytes: null,
      durationSec: null,
      error: 'Network error',
    }
  }
  const t1 = performance.now()
  const cachedHeader = res.headers.get('X-Tts-Cache')
  const cached = cachedHeader === 'hit' ? true : cachedHeader === 'miss' ? false : null

  if (res.status === 204) {
    return {
      ok: false,
      cached,
      requestToHeadersMs: t1 - t0,
      headersToBytesMs: null,
      totalMs: t1 - t0,
      sizeBytes: null,
      durationSec: null,
      error: 'TTS unavailable (204 — no voice model loaded server-side)',
    }
  }
  if (!res.ok) {
    return {
      ok: false,
      cached,
      requestToHeadersMs: t1 - t0,
      headersToBytesMs: null,
      totalMs: null,
      sizeBytes: null,
      durationSec: null,
      error: `HTTP ${res.status}`,
    }
  }

  const blob = await res.blob()
  const t2 = performance.now()
  const objectUrl = URL.createObjectURL(blob)
  const audio = new Audio(objectUrl)
  const durationSec = await new Promise<number | null>((resolve) => {
    const done = (v: number | null) => resolve(v)
    audio.addEventListener('loadedmetadata', () => done(Number.isFinite(audio.duration) ? audio.duration : null), { once: true })
    audio.addEventListener('error', () => done(null), { once: true })
    setTimeout(() => done(null), 3000)
  })
  audio.addEventListener('ended', () => URL.revokeObjectURL(objectUrl), { once: true })
  void audio.play().catch(() => {
    // Autoplay blocked (see the diagnostics panel below) — timing/cache numbers are still valid,
    // there's just no sound; the user can hit the browser's own play affordance if one appears.
  })

  return { ok: true, cached, requestToHeadersMs: t1 - t0, headersToBytesMs: t2 - t1, totalMs: t2 - t0, sizeBytes: blob.size, durationSec }
}

function fmtMs(ms: number | null): string {
  return ms == null ? '—' : `${ms.toFixed(0)}ms`
}
function fmtSec(sec: number | null): string {
  return sec == null ? '—' : `${sec.toFixed(2)}s`
}
function fmtBytes(n: number | null): string {
  if (n == null) return '—'
  return n < 1024 ? `${n}B` : `${(n / 1024).toFixed(1)}KB`
}
function fmtAgo(epochSec: number): string {
  const deltaSec = Math.max(0, Date.now() / 1000 - epochSec)
  if (deltaSec < 60) return `${deltaSec.toFixed(0)}s ago`
  if (deltaSec < 3600) return `${(deltaSec / 60).toFixed(0)}m ago`
  return `${(deltaSec / 3600).toFixed(1)}h ago`
}

// ---------------------------------------------------------------------------
// Shared reference data — players (for the template picker), the voice catalog (comparison +
// pickers, auto-discovered server-side), and the template registry (also server-side, so a
// template added later appears with no change here).
// ---------------------------------------------------------------------------

const players = ref<Player[]>([])
const voices = ref<TtsVoice[]>([])
const templates = ref<TtsTemplate[]>([])

async function loadReferenceData() {
  const [playersRes, voicesRes, templatesRes] = await Promise.all([getPlayers('', 50), getTtsVoices(), getTtsTemplates()])
  players.value = playersRes.items
  templates.value = templatesRes.items
  voices.value = voicesRes.items
}

// ---------------------------------------------------------------------------
// 1. Free-text synth box
// ---------------------------------------------------------------------------

const freeText = ref('¿Eres Luuk?')
const freeLang = ref<Lang>('es')
const freeVoiceId = ref<string>('') // '' = default voice for freeLang
const freeBusy = ref(false)
const freeResult = ref<SynthResult | null>(null)

async function speakFree() {
  const text = freeText.value.trim()
  if (!text || freeBusy.value) return
  freeBusy.value = true
  freeResult.value = null
  try {
    freeResult.value = await synthesizeAndPlay(ttsSpeakUrl(text, freeLang.value, freeVoiceId.value || null))
  } finally {
    freeBusy.value = false
    void refreshEvents()
    void refreshCache()
  }
}

// ---------------------------------------------------------------------------
// 2. Template previews — real production text-fns + cache keys (see ttsPreviewUrl's docblock in
// api.ts), so this hears the ACTUAL phrasing a real player gets, cached variants included.
// ---------------------------------------------------------------------------

const templatePlayerId = ref<number | null>(null)
const templateLang = ref<Lang>('es')
const templateBusy = reactive<Record<string, boolean>>({})
const templateResults = reactive<Record<string, SynthResult | null>>({})

async function playTemplate(templateId: string) {
  const playerId = templatePlayerId.value
  if (playerId == null || templateBusy[templateId]) return
  templateBusy[templateId] = true
  try {
    templateResults[templateId] = await synthesizeAndPlay(ttsPreviewUrl(templateId, playerId, templateLang.value))
  } finally {
    templateBusy[templateId] = false
    void refreshEvents()
    void refreshCache()
  }
}

// ---------------------------------------------------------------------------
// 3. Cache inspector
// ---------------------------------------------------------------------------

const cacheEntries = ref<TtsCacheEntry[]>([])
const cacheLoading = ref(false)
const purgingId = ref<string | null>(null)
const purgeAllOpen = ref(false)

// Dev-gate for the two mutating cache endpoints — X-Admin-Token, same guard app/routers/admin.py
// already uses for its destructive operations (reused rather than inventing a new mechanism).
// Persisted locally only (this device's browser storage), never sent anywhere but these two
// calls; nothing else on this kiosk needs it.
const ADMIN_TOKEN_KEY = 'pingpong.debug.ttsAdminToken'
const adminToken = ref('')
onMounted(() => {
  try {
    adminToken.value = localStorage.getItem(ADMIN_TOKEN_KEY) ?? ''
  } catch {
    /* storage unavailable — token box just starts empty */
  }
})
watch(adminToken, (v) => {
  try {
    localStorage.setItem(ADMIN_TOKEN_KEY, v)
  } catch {
    /* best effort */
  }
})

async function refreshCache() {
  cacheLoading.value = true
  try {
    cacheEntries.value = (await getTtsCache()).items
  } finally {
    cacheLoading.value = false
  }
}

function playCacheEntry(id: string) {
  void new Audio(ttsCacheAudioUrl(id)).play().catch(() => {
    /* autoplay blocked or entry gone — nothing else to do from a play button */
  })
}

async function purgeOne(id: string) {
  if (!adminToken.value) {
    pushToast('error', 'Set an admin token first (X-Admin-Token)')
    return
  }
  purgingId.value = id
  try {
    await purgeTtsCacheEntry(id, adminToken.value)
    pushToast('success', 'Cache entry purged')
    await refreshCache()
  } catch (e) {
    pushToast('error', e instanceof ApiRequestError ? e.message : 'Purge failed')
  } finally {
    purgingId.value = null
  }
}

async function confirmPurgeAll() {
  purgeAllOpen.value = false
  if (!adminToken.value) {
    pushToast('error', 'Set an admin token first (X-Admin-Token)')
    return
  }
  try {
    const res = await purgeAllTtsCache(adminToken.value)
    pushToast('success', `Purged ${res.purged} cache ${res.purged === 1 ? 'entry' : 'entries'}`)
    await refreshCache()
  } catch (e) {
    pushToast('error', e instanceof ApiRequestError ? e.message : 'Purge failed')
  }
}

// ---------------------------------------------------------------------------
// 4. Playback diagnostics
// ---------------------------------------------------------------------------

const canAutoplay = ref<boolean | null>(null) // null = not probed yet
// Minimal valid (0-sample) WAV — a standard "can I autoplay" probe payload, no network request.
const SILENT_WAV_DATA_URI =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA='

// Non-standard, but real (Chromium) — TS's DOM lib already types it, so a plain `in` check is
// enough; no custom interface needed (redeclaring it optional conflicts with the lib's own type).
const canSelectOutputDevice = 'setSinkId' in HTMLMediaElement.prototype
const outputDevices = ref<MediaDeviceInfo[]>([])

async function probePlaybackDiagnostics() {
  try {
    const probe = new Audio(SILENT_WAV_DATA_URI)
    probe.muted = true
    await probe.play()
    probe.pause()
    canAutoplay.value = true
  } catch {
    canAutoplay.value = false
  }
  try {
    if (navigator.mediaDevices?.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices()
      outputDevices.value = devices.filter((d) => d.kind === 'audiooutput')
    }
  } catch {
    outputDevices.value = []
  }
}

interface WindowWithWebkitAudio extends Window {
  webkitAudioContext?: typeof AudioContext
}
let toneCtx: AudioContext | null = null

function playTestTone() {
  // Deliberately bypasses TTS/fetch entirely — a pure WebAudio beep, so this verifies the
  // kiosk's speaker path independent of the server/network/Piper being involved at all.
  const Ctor = window.AudioContext ?? (window as WindowWithWebkitAudio).webkitAudioContext
  if (!Ctor) {
    pushToast('error', 'Web Audio API not available in this browser')
    return
  }
  toneCtx?.close().catch(() => {})
  const ctx = new Ctor()
  toneCtx = ctx
  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.value = 440
  osc.connect(ctx.destination)
  osc.start()
  osc.stop(ctx.currentTime + 0.4)
}

const events = ref<TtsEvent[]>([])
const eventsLoading = ref(false)

async function refreshEvents() {
  eventsLoading.value = true
  try {
    events.value = (await getTtsEvents(50)).items
  } finally {
    eventsLoading.value = false
  }
}

// ---------------------------------------------------------------------------
// 5. Voice comparison — same free text (section 1's freeText) across every installed voice.
// Voice-agnostic: the list comes from getTtsVoices() (server-side auto-discovery), so a voice
// added later to tts_models/ shows up here automatically, no frontend change.
// ---------------------------------------------------------------------------

const compareBusy = reactive<Record<string, boolean>>({})
const compareResults = reactive<Record<string, SynthResult | null>>({})

async function playCompareVoice(voice: TtsVoice) {
  const text = freeText.value.trim()
  if (!text || compareBusy[voice.id]) return
  compareBusy[voice.id] = true
  try {
    compareResults[voice.id] = await synthesizeAndPlay(ttsSpeakUrl(text, voice.lang, voice.id))
  } finally {
    compareBusy[voice.id] = false
    void refreshEvents()
    void refreshCache()
  }
}

async function playAllVoices() {
  for (const v of voices.value) {
    // Sequential — playing every voice at once would just be noise; this is meant to be listened
    // to one at a time for an A/B judgement.
    // eslint-disable-next-line no-await-in-loop
    await playCompareVoice(v)
  }
}

onMounted(() => {
  void loadReferenceData()
  void refreshCache()
  void refreshEvents()
  void probePlaybackDiagnostics()
})
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col p-4 gap-4 overflow-y-auto">
    <h1 class="text-xl font-bold">Debug — TTS / voz de salida</h1>

    <!-- 1. Free-text synth -->
    <section class="rounded-xl p-4 flex flex-col gap-3 bg-neutral-900 border border-neutral-800">
      <h2 class="text-xs font-bold text-neutral-500 uppercase tracking-wide">Free text — texto libre</h2>
      <textarea
        v-model="freeText"
        rows="2"
        placeholder="Texto a sintetizar…"
        class="w-full rounded-xl bg-neutral-800 px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
      />
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex gap-2">
          <button
            v-for="l in (['es', 'en'] as Lang[])"
            :key="l"
            type="button"
            class="tap-target px-4 rounded-xl font-semibold text-sm"
            :class="freeLang === l ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="freeLang = l"
          >
            {{ l.toUpperCase() }}
          </button>
        </div>
        <select v-model="freeVoiceId" class="tap-target rounded-xl bg-neutral-800 px-3 text-sm">
          <option value="">Voz por defecto ({{ freeLang }})</option>
          <option v-for="v in voices" :key="v.id" :value="v.id">{{ v.label }}</option>
        </select>
        <button
          type="button"
          class="tap-target px-5 rounded-xl bg-emerald-600 active:bg-emerald-500 font-semibold text-sm disabled:opacity-40"
          :disabled="!freeText.trim() || freeBusy"
          @click="speakFree"
        >
          {{ freeBusy ? 'Sintetizando…' : '▶ Speak' }}
        </button>
      </div>
      <div v-if="freeResult" class="text-xs font-mono flex flex-wrap items-center gap-x-4 gap-y-1 text-neutral-400">
        <span
          class="px-2 py-0.5 rounded-full font-sans font-semibold uppercase tracking-wide text-[10px]"
          :class="freeResult.ok ? (freeResult.cached ? 'bg-sky-700 text-white' : 'bg-emerald-700 text-white') : 'bg-red-800 text-white'"
        >
          {{ !freeResult.ok ? 'error' : freeResult.cached ? 'cache hit' : 'cold generation' }}
        </span>
        <span>request→headers {{ fmtMs(freeResult.requestToHeadersMs) }}</span>
        <span>headers→bytes {{ fmtMs(freeResult.headersToBytesMs) }}</span>
        <span>total {{ fmtMs(freeResult.totalMs) }}</span>
        <span>audio {{ fmtSec(freeResult.durationSec) }}</span>
        <span>{{ fmtBytes(freeResult.sizeBytes) }}</span>
        <span v-if="freeResult.error" class="text-red-400">{{ freeResult.error }}</span>
      </div>
    </section>

    <!-- 2. Template previews -->
    <section class="rounded-xl p-4 flex flex-col gap-3 bg-neutral-900 border border-neutral-800">
      <h2 class="text-xs font-bold text-neutral-500 uppercase tracking-wide">Template previews — frases de producción</h2>
      <div class="flex flex-wrap items-center gap-3">
        <select v-model.number="templatePlayerId" class="tap-target rounded-xl bg-neutral-800 px-3 text-sm flex-1 min-w-[160px]">
          <option :value="null" disabled>Elige un jugador…</option>
          <option v-for="p in players" :key="p.id" :value="p.id">{{ p.displayName }}</option>
        </select>
        <div class="flex gap-2">
          <button
            v-for="l in (['es', 'en'] as Lang[])"
            :key="l"
            type="button"
            class="tap-target px-4 rounded-xl font-semibold text-sm"
            :class="templateLang === l ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="templateLang = l"
          >
            {{ l.toUpperCase() }}
          </button>
        </div>
      </div>
      <p v-if="templates.length === 0" class="text-neutral-600 text-sm">No templates reported by the server.</p>
      <div v-for="t in templates" :key="t.id" class="rounded-lg bg-neutral-800/60 px-3 py-2 flex flex-col gap-1.5">
        <div class="flex items-center gap-2">
          <span class="flex-1 text-sm">{{ t.label }}</span>
          <button
            type="button"
            class="tap-target px-4 rounded-lg bg-neutral-700 active:bg-neutral-600 font-semibold text-xs disabled:opacity-40"
            :disabled="templatePlayerId == null || templateBusy[t.id]"
            @click="playTemplate(t.id)"
          >
            {{ templateBusy[t.id] ? '…' : '▶ Play' }}
          </button>
        </div>
        <div v-if="templateResults[t.id]" class="text-[10px] font-mono flex flex-wrap items-center gap-x-3 gap-y-1 text-neutral-500">
          <span
            class="px-1.5 py-0.5 rounded-full font-sans font-semibold uppercase tracking-wide"
            :class="templateResults[t.id]!.ok ? (templateResults[t.id]!.cached ? 'bg-sky-700 text-white' : 'bg-emerald-700 text-white') : 'bg-red-800 text-white'"
          >
            {{ !templateResults[t.id]!.ok ? 'error' : templateResults[t.id]!.cached ? 'cache hit' : 'cold generation' }}
          </span>
          <span>total {{ fmtMs(templateResults[t.id]!.totalMs) }}</span>
          <span>audio {{ fmtSec(templateResults[t.id]!.durationSec) }}</span>
          <span v-if="templateResults[t.id]!.error" class="text-red-400">{{ templateResults[t.id]!.error }}</span>
        </div>
      </div>
    </section>

    <!-- 3. Cache inspector -->
    <section class="rounded-xl p-4 flex flex-col gap-3 bg-neutral-900 border border-neutral-800">
      <div class="flex items-center gap-2">
        <h2 class="text-xs font-bold text-neutral-500 uppercase tracking-wide flex-1">Cache inspector</h2>
        <span class="text-[10px] text-neutral-600">{{ cacheEntries.length }} entries</span>
        <button type="button" class="tap-target px-3 rounded-lg bg-neutral-800 active:bg-neutral-700 text-xs font-semibold" @click="refreshCache">
          {{ cacheLoading ? '…' : 'Refresh' }}
        </button>
        <button
          type="button"
          class="tap-target px-3 rounded-lg bg-red-900 active:bg-red-800 text-xs font-semibold text-red-200 disabled:opacity-40"
          :disabled="cacheEntries.length === 0"
          @click="purgeAllOpen = true"
        >
          Purge all
        </button>
      </div>
      <input
        v-model="adminToken"
        type="password"
        placeholder="X-Admin-Token (required to purge)"
        class="w-full tap-target rounded-lg bg-neutral-800 px-3 text-xs font-mono outline-none focus:ring-2 focus:ring-emerald-500"
      />
      <p v-if="cacheEntries.length === 0" class="text-neutral-600 text-sm">Cache is empty.</p>
      <div class="flex flex-col gap-1 max-h-72 overflow-y-auto">
        <div
          v-for="e in cacheEntries"
          :key="e.id"
          class="rounded-lg bg-neutral-800/60 px-3 py-2 flex items-center gap-3 text-xs"
        >
          <div class="flex-1 min-w-0">
            <div class="truncate font-mono">{{ e.text ?? `(unlabeled — ${e.id.slice(0, 10)}…)` }}</div>
            <div class="text-[10px] text-neutral-500 font-mono">
              {{ e.lang ?? '?' }} / {{ e.voice ?? '?' }} &middot; {{ fmtBytes(e.sizeBytes) }} &middot; {{ fmtAgo(e.createdAt) }} &middot; {{ e.hits }} hit{{ e.hits === 1 ? '' : 's' }}
            </div>
          </div>
          <button type="button" class="tap-target px-3 rounded-lg bg-neutral-700 active:bg-neutral-600 font-semibold shrink-0" @click="playCacheEntry(e.id)">▶</button>
          <button
            type="button"
            class="tap-target px-3 rounded-lg bg-red-900 active:bg-red-800 text-red-200 font-semibold shrink-0 disabled:opacity-40"
            :disabled="purgingId === e.id"
            @click="purgeOne(e.id)"
          >
            {{ purgingId === e.id ? '…' : 'Purge' }}
          </button>
        </div>
      </div>
    </section>

    <!-- 4. Playback diagnostics -->
    <section class="rounded-xl p-4 flex flex-col gap-3 bg-neutral-900 border border-neutral-800">
      <h2 class="text-xs font-bold text-neutral-500 uppercase tracking-wide">Playback diagnostics</h2>
      <div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono text-neutral-400">
        <span>
          Autoplay:
          <span :class="canAutoplay == null ? 'text-neutral-500' : canAutoplay ? 'text-emerald-400' : 'text-red-400'">
            {{ canAutoplay == null ? 'probing…' : canAutoplay ? 'allowed' : 'blocked' }}
          </span>
        </span>
        <span>
          setSinkId (output device select):
          <span :class="canSelectOutputDevice ? 'text-emerald-400' : 'text-neutral-500'">{{ canSelectOutputDevice ? 'supported' : 'not supported' }}</span>
        </span>
        <span>Audio outputs seen: {{ outputDevices.length || 'none exposed (needs permission)' }}</span>
      </div>
      <ul v-if="outputDevices.length" class="text-[10px] text-neutral-500 font-mono list-disc list-inside">
        <li v-for="d in outputDevices" :key="d.deviceId">{{ d.label || '(unlabeled — grant mic/audio permission for names)' }}</li>
      </ul>
      <div class="flex items-center gap-3">
        <button type="button" class="tap-target px-4 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold text-sm" @click="playTestTone">
          ▶ Test tone (440Hz, no TTS)
        </button>
        <span class="text-[10px] text-neutral-600">Verifies the kiosk's speaker path independently of Piper/network.</span>
      </div>

      <div class="flex items-center gap-2 mt-1">
        <span class="text-xs font-bold text-neutral-500 uppercase tracking-wide flex-1">Last TTS events (every flow)</span>
        <span class="text-[10px] text-neutral-600">{{ events.length }}</span>
        <button type="button" class="tap-target px-3 rounded-lg bg-neutral-800 active:bg-neutral-700 text-xs font-semibold" @click="refreshEvents">
          {{ eventsLoading ? '…' : 'Refresh' }}
        </button>
      </div>
      <div class="flex flex-col gap-1 max-h-64 overflow-y-auto font-mono text-xs">
        <div v-if="events.length === 0" class="text-neutral-600">No TTS calls yet.</div>
        <div v-for="(e, i) in events" :key="i" class="flex flex-wrap items-baseline gap-x-2 text-neutral-400">
          <span class="text-neutral-600 shrink-0">{{ fmtAgo(e.ts) }}</span>
          <span class="px-1.5 py-0.5 rounded-full font-sans font-semibold shrink-0 bg-neutral-700 text-neutral-200 text-[10px]">{{ e.flow }}</span>
          <span class="truncate flex-1">"{{ e.text || '(empty)' }}"</span>
          <span class="shrink-0">[{{ e.lang }}{{ e.voice ? `/${e.voice}` : '' }}]</span>
          <span class="shrink-0" :class="e.ok ? 'text-emerald-400' : 'text-red-400'">{{ e.ok ? (e.cached ? 'hit' : 'cold') : 'fail' }}</span>
          <span v-if="e.latencyMs != null" class="shrink-0 text-neutral-600">{{ e.latencyMs.toFixed(0) }}ms</span>
          <span v-if="e.playerId != null" class="shrink-0 text-neutral-600">player #{{ e.playerId }}</span>
        </div>
      </div>
    </section>

    <!-- 5. Voice comparison -->
    <section class="rounded-xl p-4 flex flex-col gap-3 bg-neutral-900 border border-neutral-800">
      <div class="flex items-center gap-2">
        <h2 class="text-xs font-bold text-neutral-500 uppercase tracking-wide flex-1">Voice comparison</h2>
        <button
          type="button"
          class="tap-target px-3 rounded-lg bg-neutral-800 active:bg-neutral-700 text-xs font-semibold disabled:opacity-40"
          :disabled="!freeText.trim() || voices.length < 2"
          @click="playAllVoices"
        >
          ▶ Play all (uses the text above)
        </button>
      </div>
      <p v-if="voices.length === 0" class="text-neutral-600 text-sm">No voices reported by the server (TTS unavailable or no models installed).</p>
      <p v-else-if="voices.length === 1" class="text-neutral-600 text-sm">Only one voice installed ({{ voices[0].label }}) — nothing to compare yet.</p>
      <div v-else class="flex flex-col gap-1.5">
        <div v-for="v in voices" :key="v.id" class="rounded-lg bg-neutral-800/60 px-3 py-2 flex items-center gap-3 text-xs">
          <span class="flex-1 min-w-0 truncate">
            {{ v.label }}
            <span v-if="v.isDefault" class="ml-1 text-[10px] text-emerald-500 font-semibold uppercase tracking-wide">default</span>
          </span>
          <button
            type="button"
            class="tap-target px-4 rounded-lg bg-neutral-700 active:bg-neutral-600 font-semibold shrink-0 disabled:opacity-40"
            :disabled="!freeText.trim() || compareBusy[v.id]"
            @click="playCompareVoice(v)"
          >
            {{ compareBusy[v.id] ? '…' : '▶' }}
          </button>
          <span v-if="compareResults[v.id]" class="font-mono text-[10px] text-neutral-500 shrink-0 w-32 text-right">
            {{ compareResults[v.id]!.ok ? fmtMs(compareResults[v.id]!.totalMs) : (compareResults[v.id]!.error ?? 'error') }}
          </span>
        </div>
      </div>
    </section>

    <ConfirmDialog
      :open="purgeAllOpen"
      title="Purge the entire TTS cache?"
      body="Every cached WAV is deleted. The next request for each phrase regenerates it (well under 1s) — this doesn't break anything, it just costs a cold generation on next use."
      confirm-label="Purge all"
      danger
      @confirm="confirmPurgeAll"
      @cancel="purgeAllOpen = false"
    />
  </div>
</template>
